import React from "react";
import styled from "@emotion/styled";

import {
  DEFAULT_FILTERS as DEFAULT_FRAMEWORK_FILTERS,
  frameworksUrl,
} from "components/framework/utils";
import { ViewConfigurationFilterSettingsRelevanceScoreEnum as RelevanceScoreEnum } from "lib/generated/app-api";
import { OpportunityDetails } from "../../../lib/hooks/api/opportunities/useOpportunity";
import { DocumentCategory } from "../../../lib/types/graphQLEnums";
import { ALL_KEYWORDS_TOKEN } from "../../../lib/types/models";
import { Flex } from "../../../styles/utility-components";
import { generateContactsUrl } from "../../contacts/utils";
import {
  DEFAULT_DOCUMENT_FILTERS,
  documentsUrl,
  simplifiedDocumentTypes,
} from "../../documents/utils";
import { generateNoticeSearchUrl } from "../../notices/utils";
import OpportunityBuyers from "../saved/OpportunityBuyers";
import { OpportunityContacts } from "../saved/OpportunityContacts";
import OpportunityDocuments from "../saved/OpportunityDocuments";
import OpportunityFrameworks from "../saved/OpportunityFrameworks";
import { OpportunityNotices } from "../saved/OpportunityNotices";
import OpportunitySavedRow from "../saved/OpportunitySavedRow";
import { OpportunitySuppliers } from "../saved/OpportunitySuppliers";

type OpportunitySavedSidebarProps = {
  opportunity: OpportunityDetails;
};

const getEntitySearchUrls = (opportunity?: OpportunityDetails): Record<string, string> => {
  if (!opportunity) return {};

  const buyerIds = opportunity.buyers?.map((buyer) => buyer.id) ?? [];
  const relevantDocumentCategories = simplifiedDocumentTypes
    .filter((type) => type.value !== DocumentCategory.Other)
    .map((type) => type.value);

  const noticesSearchUrl = generateNoticeSearchUrl({
    relevanceScore: [RelevanceScoreEnum.Low, RelevanceScoreEnum.Medium, RelevanceScoreEnum.High],
    buyers: buyerIds,
  });

  const contactsSearchUrl = generateContactsUrl({ buyerGuids: buyerIds });

  const documentsSearchUrl = documentsUrl({
    ...DEFAULT_DOCUMENT_FILTERS,
    organisationIds: buyerIds,
    categories: relevantDocumentCategories,
  });

  const frameworksSearchUrl = frameworksUrl({
    ...DEFAULT_FRAMEWORK_FILTERS,
    buyerIds: buyerIds,
    keywords: [ALL_KEYWORDS_TOKEN],
  });

  return {
    noticesSearchUrl: noticesSearchUrl,
    contactsSearchUrl: contactsSearchUrl,
    documentsSearchUrl: documentsSearchUrl,
    frameworksSearchUrl: frameworksSearchUrl,
  };
};

export default function OpportunitySavedSidebar({ opportunity }: OpportunitySavedSidebarProps) {
  const { noticesSearchUrl, contactsSearchUrl, documentsSearchUrl, frameworksSearchUrl } =
    getEntitySearchUrls(opportunity);
  return (
    <Container aria-label="saved-sidebar">
      <OpportunitySavedRow entity="notice" entityCount={opportunity?.notices.length ?? 0}>
        <OpportunityNotices
          notices={opportunity?.notices ?? []}
          entityUrl={noticesSearchUrl}
          opportunityId={opportunity.id}
        />
      </OpportunitySavedRow>
      <OpportunitySavedRow entity="buyer" entityCount={opportunity?.buyers.length ?? 0}>
        <OpportunityBuyers buyers={opportunity?.buyers ?? []} opportunityId={opportunity.id} />
      </OpportunitySavedRow>
      <OpportunitySavedRow entity="supplier" entityCount={opportunity?.suppliers.length ?? 0}>
        <OpportunitySuppliers
          suppliers={opportunity?.suppliers ?? []}
          opportunityId={opportunity.id}
        />
      </OpportunitySavedRow>
      <OpportunitySavedRow entity="contact" entityCount={opportunity?.contacts.length ?? 0}>
        <OpportunityContacts
          contacts={opportunity?.contacts ?? []}
          entityUrl={contactsSearchUrl}
          opportunityId={opportunity.id}
        />
      </OpportunitySavedRow>
      <OpportunitySavedRow entity="document" entityCount={opportunity?.documents.length ?? 0}>
        <OpportunityDocuments
          documents={opportunity?.documents ?? []}
          entityUrl={documentsSearchUrl}
          opportunityId={opportunity.id}
        />
      </OpportunitySavedRow>
      <OpportunitySavedRow entity="framework" entityCount={opportunity?.frameworks.length ?? 0}>
        <OpportunityFrameworks
          frameworks={opportunity?.frameworks ?? []}
          entityUrl={frameworksSearchUrl}
          opportunityId={opportunity.id}
        />
      </OpportunitySavedRow>
    </Container>
  );
}

const Container = styled(Flex)({
  flex: "1 1 0",
  flexDirection: "column",
  gap: 0,
  overflowY: "auto",
  height: "100%",
  minHeight: 0,
  position: "relative",
  "& > *": {
    flexShrink: 0,
  },
});
